/**
 * Chart Dimensions Hook
 * Calculates chart dimensions based on data and lanes with responsive support
 */

import { useMemo } from 'react';
import type { TruckTimelineData } from '../types';
import { assignLanes } from '../utils';
import {
  CHART_PADDING,
  BASE_ROW_HEIGHT,
  LANE_HEIGHT,
  LANE_SPACING,
  MIN_CHART_HEIGHT,
  CHART_WIDTH,
} from '../constants';

interface UseChartDimensionsProps {
  data: TruckTimelineData[];
  propHeight?: number;
  isMobile?: boolean;
  isSmallMobile?: boolean;
}

export const useChartDimensions = ({
  data,
  propHeight,
  isMobile = false,
  isSmallMobile = false,
}: UseChartDimensionsProps) => {
  // Determine device type for responsive values
  const deviceType = isSmallMobile ? 'mobile' : isMobile ? 'tablet' : 'desktop';

  // Get responsive values
  const padding = CHART_PADDING[deviceType];
  const baseRowHeight = BASE_ROW_HEIGHT[deviceType];
  const laneHeight = LANE_HEIGHT[deviceType];
  const chartWidth = CHART_WIDTH[deviceType];

  const totalRowHeight = useMemo(() => {
    return data.reduce((total, truckData) => {
      const periodsWithLanes = assignLanes(truckData.periods);
      const maxLane = periodsWithLanes.reduce((max, p) => Math.max(max, p.lane), 0);
      const lanesNeeded = maxLane + 1;
      return total + Math.max(baseRowHeight, lanesNeeded * (laneHeight + LANE_SPACING));
    }, 0);
  }, [data, baseRowHeight, laneHeight]);

  const calculatedHeight = Math.max(
    MIN_CHART_HEIGHT,
    totalRowHeight + padding.top + padding.bottom
  );

  const finalChartHeight = propHeight || calculatedHeight;
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = finalChartHeight - padding.top - padding.bottom;

  return {
    chartHeight: finalChartHeight,
    chartWidth,
    innerWidth,
    innerHeight,
    padding,
    deviceType,
  };
};
